import { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import EnvironmentToggle from '../EnvironmentToggle';
import SandboxBanner from '../SandboxBanner';
import sandboxStyles from '../SandboxBanner.module.css';
import { useEnvironment } from '../../context/EnvironmentContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { api } from '../../services/api';
import styles from './AdminLayout.module.css';

// SVG Icon Components
const DashboardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="7" height="7" rx="1"></rect>
    <rect x="14" y="3" width="7" height="7" rx="1"></rect>
    <rect x="14" y="14" width="7" height="7" rx="1"></rect>
    <rect x="3" y="14" width="7" height="7" rx="1"></rect>
  </svg>
);

const ProductsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
  </svg>
);

const ResourcesIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
  </svg>
);

const OrdersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <path d="M16 10a4 4 0 0 1-8 0"></path>
  </svg>
);

const ClientsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);
const AgenciesIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 21h18"></path>
    <path d="M9 8h1"></path>
    <path d="M9 12h1"></path>
    <path d="M9 16h1"></path>
    <path d="M14 8h1"></path>
    <path d="M14 12h1"></path>
    <path d="M14 16h1"></path>
    <path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16"></path>
  </svg>
);

const IntakeFormsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);

const TasksIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 11l3 3L22 4"></path>
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
  </svg>
);

const SupportIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
  </svg>
);

const RolesIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
  </svg>
);


const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3"></circle>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
  </svg>
);

const PlansIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
    <path d="M3 9h18"></path>
    <path d="M9 21V9"></path>
  </svg>
);

const CSVIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <path d="M8 13h2" />
    <path d="M8 17h2" />
    <path d="M14 13h2" />
    <path d="M14 17h2" />
  </svg>
);

const PromoCodesIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const GiftCardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M2 11h20" />
    <path d="M12 7v14" />
    <path d="M7.5 7a2.5 2.5 0 0 1 0-5C10 2 12 7 12 7" />
    <path d="M16.5 7a2.5 2.5 0 0 0 0-5C14 2 12 7 12 7" />
  </svg>
);

const WelcomeCallIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const AbandonedIntakeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <line x1="18" y1="8" x2="23" y2="13" />
    <line x1="23" y1="8" x2="18" y2="13" />
  </svg>
);

const ReceivableIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const OvalCareSyncIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 2v6h-6" />
    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
    <path d="M3 22v-6h6" />
    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
  </svg>
);

const RevenueCalculationIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 3v18h18" />
    <path d="M7 15l4-5 3 3 5-7" />
    <path d="M19 6h-3" />
  </svg>
);

// Nav entries the grouped IA folds into another surface. They stay DEFINED in navSections below,
// so restoring one is a single edit HERE — delete its label and the entry returns to the sidebar.
// Hiding is a NAV concern only: every route and permission string is left exactly as it was.
const HIDDEN_NAV_LABELS = [
  'Tasks',                // folded into Support
  'Revenue Calculation',  // hidden, not deleted
  'Abandoned Intakes',    // hidden, not deleted
  'Promo Codes',          // folded into Offers & Credits
  'Gift Cards',           // folded into Offers & Credits
  'CSVs Received',        // folded into Data Imports
];

const AdminLayout = () => {
  const { user, logout, isAgencyAdmin, hasPermission, isLoading, refreshUser } = useAuth();
  // Environment is a property of the ORIGIN this bundle was served from, never client state.
  const { isSandbox } = useEnvironment();
  // Prefixes document.title with [SANDBOX] across every admin route in sandbox.
  useDocumentTitle();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isEnteringTestMode, setIsEnteringTestMode] = useState(false);
  const navRef = useRef(null);

  // Super Admin holds this implicitly: hasPermission short-circuits to true on isSystem.
  const canEnterTestDashboard = hasPermission('test_admin.view');

const updateScrollIndicators = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;

    const { scrollLeft, scrollWidth, clientWidth } = nav;
    setCanScrollLeft(scrollLeft > 5);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    updateScrollIndicators();
    nav.addEventListener('scroll', updateScrollIndicators);
    window.addEventListener('resize', updateScrollIndicators);

    return () => {
      nav.removeEventListener('scroll', updateScrollIndicators);
      window.removeEventListener('resize', updateScrollIndicators);
    };
  }, [updateScrollIndicators]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Enter the sandboxed test dashboard. This is attached to the ONE existing
  // "Back to Dashboard" link — there is deliberately no second sidebar entry.
  // Sandbox state is flipped server-side first, then refreshUser() re-reads /auth/me so the
  // banner on /dashboard is driven by the database rather than by client memory.
  // Fail-CLOSED: if the enter call does not succeed we stay on /admin, because landing an
  // admin on the REAL dashboard while they believe they are sandboxed is the one outcome
  // that silently writes live data.
  // Entering the test dashboard switches which API SURFACE this client talks to:
  // refreshUser() picks up sandboxMode from /auth/me, and AuthContext's setter
  // mirrors it into web/services/api.js so subsequent calls target /api/sandbox.
  // The POST itself is pinned to the LIVE surface (it is an admin-panel action).
  // Fail-closed: on any error we stay put rather than navigate into a dashboard
  // that might still be pointed at the live surface.
  const handleEnterTestDashboard = async (event) => {
    event.preventDefault();
    if (isEnteringTestMode) return;
    setIsEnteringTestMode(true);
    try {
      await api.post('/admin/test-admin/enter', {});
      await refreshUser();
      navigate('/dashboard');
    } catch {
      // A 403 here means the permission was revoked since page load. AuthContext's 403
      // interceptor refetches /auth/me, which drops test_admin.view and hides this entry.
    } finally {
      setIsEnteringTestMode(false);
    }
  };

  const navSections = [
    {
      label: 'Operations',
      items: [
        { path: '/admin', label: 'MAX Insight', icon: <DashboardIcon />, exact: true, isMaxInsight: true },
        { path: '/admin/members', permission: 'clients.view', label: 'Clients', icon: <ClientsIcon /> },
        { path: '/admin/orders', permission: 'orders.view', label: 'Orders', icon: <OrdersIcon /> },
        { path: '/admin/support-tickets', permission: 'support_tickets.view', label: 'Support', icon: <SupportIcon /> },
        { path: '/admin/tasks', permission: 'tasks.view', label: 'Tasks', icon: <TasksIcon /> },
      ]
    },
    {
      label: 'Commerce',
      items: [
        { path: '/admin/products', permission: 'products.view', label: 'Products', icon: <ProductsIcon /> },
        { path: '/admin/intake-forms', permission: 'intake_forms.view', label: 'Intake PRO', icon: <IntakeFormsIcon />, isPro: true },
        { path: '/admin/plans', permission: 'plans.view', label: 'Plans', icon: <PlansIcon /> },
        { path: '/admin/offers', permission: 'promo_codes.view', label: 'Offers & Credits', icon: <PromoCodesIcon /> },
        { path: '/admin/resources', permission: 'resources.view', label: 'Member Resources', icon: <ResourcesIcon /> },
        { path: '/admin/promo-codes', permission: 'promo_codes.view', label: 'Promo Codes', icon: <PromoCodesIcon /> },
        { path: '/admin/gift-cards', permission: 'gift_cards.view', label: 'Gift Cards', icon: <GiftCardIcon /> },
      ]
    },
    {
      label: 'Finance',
      items: [
        { path: '/admin/accounts-receivable', permission: 'accounts_receivable.view', label: 'Accounts Receivable', icon: <ReceivableIcon /> },
        { path: '/admin/transactions', permission: 'orders.view', label: 'Transactions', icon: <ReceivableIcon /> },
        { path: '/admin/revenue-calculation', permission: 'revenue_calculation.view', label: 'Revenue Calculation', icon: <RevenueCalculationIcon /> },
      ]
    },
    {
      label: 'Partners & Data',
      items: [
        { path: '/admin/agencies', permission: 'agencies.view', label: 'Entities', icon: <AgenciesIcon /> },
        { path: '/admin/data-imports', permission: 'csv_received.view', label: 'Data Imports', icon: <CSVIcon /> },
        { path: '/admin/oval-care-sync', permission: 'oval_care_sync.view', label: 'Pet Benefits Sync', icon: <OvalCareSyncIcon /> },
        { path: '/admin/csv-received', permission: 'csv_received.view', label: 'CSVs Received', icon: <CSVIcon /> },
      ]
    },
    {
      label: 'Outreach',
      items: [
        { path: '/admin/welcome-calls', permission: 'welcome_calls.view', label: 'Welcome Calls', icon: <WelcomeCallIcon /> },
        { path: '/admin/abandoned-intakes', permission: 'abandoned_intakes.view', label: 'Abandoned Intakes', icon: <AbandonedIntakeIcon /> },
      ]
    },
    {
      label: 'Administration',
      items: [
        { path: '/admin/roles', permission: 'roles.view', label: 'Roles & Permissions', icon: <RolesIcon /> },
        { path: '/admin/activity-logs', permission: 'activity_logs.view', label: 'Activity Logs', icon: <SettingsIcon /> },
        { path: '/admin/settings', permission: 'settings.view', label: 'Settings', icon: <SettingsIcon /> },
      ]
    }
  ];

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  // Agency admins can only access dashboard and members
  if (isAgencyAdmin) {
    const path = location.pathname;
    const allowed = path === '/admin' || path.startsWith('/admin/members');
    if (!allowed) {
      return <Navigate to="/admin" replace />;
    }
  }

  return (
    <div className={styles.layout}>
      {/* Top Header */}
      <header className={`${styles.header} ${isScrolled ? styles.headerScrolled : ''}`}>
        <div className={styles.headerLeft}>
          <button
            className={styles.menuBtn}
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          <Link to="/admin" className={styles.logo}>
            <img loading="eager" src="/oval-logo.webp" alt="OVAL" className={styles.logoImage} />
            <span className={styles.adminBadge}>{isAgencyAdmin ? "Agency" : "Admin"}</span>
          </Link>
          <div className={styles.envToggleSlot}>
            <EnvironmentToggle />
          </div>
          {isSandbox && <span className={sandboxStyles.badge} data-environment-badge="sandbox">SANDBOX</span>}
        </div>

        <div className={styles.headerRight}>
          <div className={styles.userMenu}>
            <span className={styles.userName}>{user?.email}</span>
            <button onClick={handleLogout} className={styles.logoutBtn}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className={styles.container}>
        {/* Sidebar */}
        <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''} ${isScrolled ? styles.sidebarScrolled : ''}`}>
          {/* Scroll indicators for mobile */}
          <div className={`${styles.scrollIndicatorLeft} ${canScrollLeft ? styles.visible : ''}`} />
          <div className={`${styles.scrollIndicatorRight} ${canScrollRight ? styles.visible : ''}`} />

          <nav ref={navRef} className={styles.nav}>
            {/* Test Dashboard — the permission-gated entry into the sandboxed member view.
                This IS the former ungated "Back to Dashboard" link, now gated and wired to the
                enter endpoint. Hidden entirely while permissions load, so it can never flash
                for a user who turns out not to hold test_admin.view. */}
            {canEnterTestDashboard && (
              <Link
                to="/dashboard"
                onClick={handleEnterTestDashboard}
                className={styles.backToDashboard}
                aria-label="Test Dashboard"
                aria-busy={isEnteringTestMode}
                title="Test Dashboard"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5"></path>
                  <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
                <span>Test Dashboard</span>
              </Link>
            )}

            {isLoading ? (
              /* No-flash: while permissions are loading, show a neutral skeleton — never the
                 old "show everything until perms load" nav, which flashed unauthorized items. */
              <div className={styles.navSection} aria-hidden="true">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      height: '38px',
                      margin: '4px 12px',
                      borderRadius: '8px',
                      background: 'var(--color-tan-100, #fdf9f3)',
                      border: '0.5px solid var(--color-tan-300, #f3e5d0)',
                      opacity: 0.55
                    }}
                  />
                ))}
              </div>
            ) : (isAgencyAdmin
              ? [{ label: 'Operations', items: [
                  { path: '/admin', permission: 'dashboard.view', label: 'Dashboard', icon: <DashboardIcon />, exact: true },
                  { path: '/admin/members', permission: 'clients.view', label: 'Clients', icon: <ClientsIcon /> },
                ]}]
              : navSections
            ).map((section, sectionIndex) => {
              // Strict filtering: an item renders only if it is not folded away by
              // HIDDEN_NAV_LABELS, and it needs no permission, the caller is an agency admin
              // (fixed-capability legacy role, gated by the redirect above), or the user actually
              // holds the permission. isSystem users hold all (hasPermission).
              const visibleItems = section.items.filter((item) =>
                !HIDDEN_NAV_LABELS.includes(item.label) &&
                (!item.permission || isAgencyAdmin || hasPermission(item.permission))
              );
              if (visibleItems.length === 0) return null;
              return (
              <div key={sectionIndex} className={`${styles.navSection} ${section.isPremiumSection ? styles.premiumSection : ''} ${section.isMonsterSection ? styles.monsterSection : ''}`}>
                {section.label && (
                  <span className={`${styles.sectionLabel} ${section.isPremiumSection ? styles.premiumLabel : ''} ${section.isMonsterSection ? styles.monsterLabel : ''}`}>{section.label}</span>
                )}
                {visibleItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`${styles.navLink} ${
                      isActive(item) ? styles.active : ''
                    } ${item.isMaxInsight ? styles.maxInsightLink : ''} ${item.isPro ? styles.proLink : ''} ${item.isGold ? styles.goldLink : ''} ${item.isGreen ? styles.greenLink : ''} ${item.isBlue ? styles.blueLink : ''} ${item.isPurple ? styles.purpleLink : ''}`}
                  >
                    <span className={styles.navIcon}>{item.icon}</span>
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
              ); })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className={styles.main} data-admin-main>
          <SandboxBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;

